import cookies from 'js-cookie';

export type Persistor<T> = {
  get: (slide?: boolean) => T | undefined;
  set: (value: T | undefined) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const voidPersister: Persistor<any> = {
  get: () => undefined,
  set: () => {
    // void
  },
};

type StorageItem<T> = {
  value: T;
  timestamp: number;
};

const storageDomain = 'il-dat';

export const initStoragePersistor = <T>(ttlMs: number, storageKey: string): Persistor<T> => {
  let storageEnabled = false;

  try {
    localStorage.setItem(`${storageDomain}-test-item`, 'test');
    localStorage.removeItem(`${storageDomain}-test-item`);
    storageEnabled = true;
  } catch {
    storageEnabled = false;
  }

  if (!storageEnabled) return voidPersister;

  const key = `${storageDomain}-${storageKey}`;
  const set = (value: T | undefined) => {
    if (value) {
      const item: StorageItem<T> = {
        value,
        timestamp: new Date().getTime(),
      };
      localStorage.setItem(key, JSON.stringify(item));
    } else {
      localStorage.removeItem(key);
    }
  };

  return {
    get: (slide = true) => {
      const entry = localStorage.getItem(key);
      if (!entry) return undefined;

      try {
        const { timestamp, value } = JSON.parse(entry) as StorageItem<T>;
        if (timestamp + ttlMs < new Date().getTime()) return undefined;

        if (slide) set(value);
        return value;
      } catch {
        localStorage.removeItem(key);
        return undefined;
      }
    },
    set,
  };
};

export const initCookiePersistor = <T>(ttlMs: number, storageKey: string): Persistor<T> => {
  let cookieEnabled = window.navigator.cookieEnabled;

  if (!cookieEnabled) {
    cookies.set(`${storageDomain}-test-cookie`, 'test', { secure: true, sameSite: 'strict' });
    cookieEnabled = document.cookie.includes(`${storageDomain}-test-cookie`);
    cookies.remove(`${storageDomain}-test-cookie`);
  }

  if (!cookieEnabled) return voidPersister;

  const key = `${storageDomain}-${storageKey}`;
  const set = (value: T | undefined) => {
    if (value) {
      const expires = new Date(new Date().getTime() + ttlMs);
      cookies.set(key, JSON.stringify(value), { expires, secure: true, sameSite: 'strict' });
    } else {
      cookies.remove(key);
    }
  };

  return {
    get: (slide = true) => {
      const stringValue = cookies.get(key);
      if (!stringValue) return undefined;

      try {
        const value = JSON.parse(stringValue) as T;

        if (slide) set(value);
        return value;
      } catch {
        cookies.remove(key);
        return undefined;
      }
    },
    set,
  };
};

export const initMemoryPersistor = <T>(ttlMs: number): Persistor<T> => {
  let value: T | undefined;
  let timestamp = 0;

  return {
    get: (slide = true) => {
      const currentTime = new Date().getTime();
      if (timestamp + ttlMs < currentTime) return undefined;
      if (slide) {
        timestamp = currentTime;
      }

      return value;
    },
    set: newValue => {
      value = newValue;
      timestamp = new Date().getTime();
    },
  };
};

export const initChainPersistor = <T>(...childPersistors: Persistor<T>[]) => {
  const set = (value: T | undefined) => childPersistors.forEach(persistor => persistor.set(value));

  return {
    get: (slide = true) => {
      let value: T | undefined;
      for (const persistor of childPersistors) {
        value = persistor.get(false);
        if (value !== undefined) break;
      }

      if (slide && value) set(value);
      return value;
    },
    set,
  };
};

export default <T>({
  ttlSeconds = 365 * 24 * 60 * 60,
  storageKey,
}: {
  ttlSeconds?: number;
  storageKey: string;
}): Persistor<T> => {
  const ttlMs = ttlSeconds * 1000;

  return initChainPersistor(
    initStoragePersistor(ttlMs, storageKey),
    initCookiePersistor(ttlMs, storageKey),
    initMemoryPersistor(ttlMs),
  );
};
